import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { clearOfflineTrips } from "@/lib/offline-trips";

const PROTECTED_PATHS = ["/itinerary", "/discussion", "/my-trips", "/my-lists", "/family", "/settings", "/admin", "/summary"];

// Lightweight i18n helper for module-level toasts (without React context).
function tMsg(key: "sessionExpired" | "signedOut" | "appLoading"): string {
  let lang = "fr";
  try {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("lang") : null;
    if (stored === "en" || stored === "es" || stored === "fr") lang = stored;
  } catch { /* ignore */ }
  const dict: Record<string, Record<string, string>> = {
    fr: {
      sessionExpired: "Votre session a expiré. Vous êtes déconnecté.",
      signedOut: "Vous êtes déconnecté",
      appLoading: "Chargement…",
    },
    en: {
      sessionExpired: "Your session has expired. You've been signed out.",
      signedOut: "You've been signed out",
      appLoading: "Loading…",
    },
    es: {
      sessionExpired: "Tu sesión ha caducado. Se cerró tu sesión.",
      signedOut: "Has cerrado sesión",
      appLoading: "Cargando…",
    },
  };
  return dict[lang][key];
}

// Module-scope guard: prevents duplicate "session expired" toasts/redirects
// even across multiple AuthProvider mounts or fetch interceptors firing in parallel.
let sessionExpiredHandled = false;

export function handleSessionExpired() {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  toast.info(tMsg("sessionExpired"));
  const path = window.location.pathname;
  const needsAuth = PROTECTED_PATHS.some((p) => path.startsWith(p));
  // Small delay so the toast is visible before the hard navigation
  setTimeout(() => {
    window.location.replace(needsAuth ? `/auth?redirect=${encodeURIComponent(path)}` : "/");
  }, 400);
}

/** Test-only helper: reset the module-scoped guard between tests. */
export function __resetSessionExpiredGuard() {
  sessionExpiredHandled = false;
}

// Install a one-time global fetch interceptor that detects expired/invalid
// Supabase JWTs (401 + invalid_token / jwt expired) and triggers the same flow.
let fetchInterceptorInstalled = false;
function installFetchInterceptor() {
  if (fetchInterceptorInstalled || typeof window === "undefined") return;
  fetchInterceptorInstalled = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      if (response.status === 401) {
        const url = typeof args[0] === "string" ? args[0] : (args[0] as Request).url;
        // Only react to Supabase API calls
        const supaUrl = (import.meta as any).env?.VITE_SUPABASE_URL || "";
        if (supaUrl && url.startsWith(supaUrl)) {
          // Peek at the body without consuming the original response
          const cloned = response.clone();
          const text = await cloned.text().catch(() => "");
          if (/jwt|token|expired|invalid/i.test(text)) {
            handleSessionExpired();
          }
        }
      }
    } catch {
      // never break the original request flow
    }
    return response;
  };
}

interface SubscriptionState {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
}

export type PlanTier = "free" | "voyageur" | "explorer" | "pro";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  subscription: SubscriptionState;
  refreshSubscription: () => Promise<void>;
  plan: PlanTier;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
  subscription: { subscribed: false, productId: null, subscriptionEnd: null, loading: true },
  refreshSubscription: async () => {},
  plan: "free",
  isAdmin: false,
});

export const useAuth = () => useContext(AuthContext);

// Stripe product/price mapping — pricing v2 (Voyageur / Explorer / Pro / Single search)
export const STRIPE_PLANS = {
  voyageurMonthly: {
    productId: "prod_UV1n45SO6AtKrF",
    priceId: "price_1TW1kB1p5v6F2yGAl4QuuC8j",
    name: "Voyageur (mensuel)",
    price: "6,99 €/mois",
  },
  voyageurYearly: {
    productId: "prod_UV1nLXVsw7DmRq",
    priceId: "price_1TW1kD1p5v6F2yGAJMT9ZI5I",
    name: "Voyageur (annuel)",
    price: "49 €/an",
  },
  explorerMonthly: {
    productId: "prod_UV1nS251KC70D0",
    priceId: "price_1TW1kF1p5v6F2yGAlGP7IEdV",
    name: "Explorer (mensuel)",
    price: "12,99 €/mois",
  },
  explorerYearly: {
    productId: "prod_UV1neCjXxSoG9e",
    priceId: "price_1TW1kG1p5v6F2yGAxB5w7hXZ",
    name: "Explorer (annuel)",
    price: "99 €/an",
  },
  proMonthly: {
    productId: "prod_UV1ns1xZyh4GW9",
    priceId: "price_1TW1kJ1p5v6F2yGAmEVuA0CI",
    name: "Pro / Agence (mensuel)",
    price: "29,99 €/mois",
  },
  proYearly: {
    productId: "prod_UV1nA0SsAsI1Mx",
    priceId: "price_1TW1kK1p5v6F2yGAj3K2gyNG",
    name: "Pro / Agence (annuel)",
    price: "249 €/an",
  },
  singleSearch: {
    productId: "prod_USsQdMMWM0R0Jl",
    priceId: "price_1TTwfW0uHa1YT0od8Nn9tsDj",
    name: "Recherche unique (7 jours)",
    price: "5 €",
  },
} as const;

// New plan tier system. Legacy "explorer"/"family" remain as type values for
// backwards-compat but legacy product IDs now map to the equivalent v2 tier:
//   legacy Explorer 8,99 €  → voyageur (entry tier)
//   legacy Famille 15,99 €  → explorer (mid tier)

// New live product IDs (EUR only)
export const VOYAGEUR_MONTHLY_PRODUCT_ID = "prod_UV1n45SO6AtKrF";
export const VOYAGEUR_YEARLY_PRODUCT_ID = "prod_UV1nLXVsw7DmRq";
export const EXPLORER_MONTHLY_PRODUCT_ID = "prod_UV1nS251KC70D0";
export const EXPLORER_YEARLY_PRODUCT_ID = "prod_UV1neCjXxSoG9e";
export const PRO_MONTHLY_PRODUCT_ID = "prod_UV1ns1xZyh4GW9";
export const PRO_YEARLY_PRODUCT_ID = "prod_UV1nA0SsAsI1Mx";

// Legacy product IDs (kept for users still subscribed under old prices)
export const LEGACY_EXPLORER_MONTHLY_PRODUCT_ID = "prod_USsQuDFH0BhVlJ"; // 8,99 €
export const LEGACY_EXPLORER_YEARLY_PRODUCT_ID = "prod_USsQgHLi8RvZbp"; // 89,90 €
export const LEGACY_FAMILY_MONTHLY_PRODUCT_ID = "prod_USsQYmt0EBtxHw"; // 15,99 €
export const LEGACY_FAMILY_YEARLY_PRODUCT_ID = "prod_USsQODo9ps1GK0"; // 159,90 €

export function getPlanFromProductId(productId: string | null): PlanTier {
  if (productId === PRO_MONTHLY_PRODUCT_ID || productId === PRO_YEARLY_PRODUCT_ID) return "pro";
  if (
    productId === EXPLORER_MONTHLY_PRODUCT_ID ||
    productId === EXPLORER_YEARLY_PRODUCT_ID ||
    productId === LEGACY_FAMILY_MONTHLY_PRODUCT_ID ||
    productId === LEGACY_FAMILY_YEARLY_PRODUCT_ID
  ) return "explorer";
  if (
    productId === VOYAGEUR_MONTHLY_PRODUCT_ID ||
    productId === VOYAGEUR_YEARLY_PRODUCT_ID ||
    productId === LEGACY_EXPLORER_MONTHLY_PRODUCT_ID ||
    productId === LEGACY_EXPLORER_YEARLY_PRODUCT_ID
  ) return "voyageur";
  return "free";
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionState>({
    subscribed: false,
    productId: null,
    subscriptionEnd: null,
    loading: true,
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [compPlan, setCompPlan] = useState<PlanTier | null>(null);

  const fetchAdminRole = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
      if (!error) return !!data;

      const { data: role } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin" as any)
        .maybeSingle();
      return !!role;
    } catch {
      return false;
    }
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      setSubscription({
        subscribed: data?.subscribed ?? false,
        productId: data?.product_id ?? null,
        subscriptionEnd: data?.subscription_end ?? null,
        loading: false,
      });
    } catch {
      setSubscription(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchCompPlan = useCallback(async (userId: string): Promise<PlanTier | null> => {
    try {
      const { data } = await supabase
        .from("complimentary_plans" as any)
        .select("plan, expires_at")
        .eq("user_id", userId)
        .maybeSingle();
      if (!data) return null;
      const row = data as unknown as { plan: string; expires_at: string | null };
      if (row.expires_at && new Date(row.expires_at).getTime() < Date.now()) return null;
      if (row.plan === "voyageur" || row.plan === "explorer" || row.plan === "pro") return row.plan;
      return null;
    } catch {
      return null;
    }
  }, []);

  // Check admin role when session changes
  useEffect(() => {
    if (!session?.user?.id) {
      setIsAdmin(false);
      setCompPlan(null);
      return;
    }
    let cancelled = false;
    fetchAdminRole(session.user.id).then((hasRole) => {
      if (!cancelled) setIsAdmin(hasRole);
    });
    fetchCompPlan(session.user.id).then((p) => {
      if (!cancelled) setCompPlan(p);
    });
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, fetchAdminRole, fetchCompPlan]);

  useEffect(() => {
    if (!session?.user?.id) return;

    const userId = session.user.id;
    const channel = supabase
      .channel(`admin-role-${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "user_roles", filter: `user_id=eq.${userId}` }, async () => {
        setIsAdmin(await fetchAdminRole(userId));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id, fetchAdminRole]);

  useEffect(() => {
    installFetchInterceptor();
    let hadSession = false;
    let initialized = false;

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setLoading(false);
      if (session) {
        hadSession = true;
        // Reset the guard once a fresh session is in place
        sessionExpiredHandled = false;
        setTimeout(() => refreshSubscription(), 0);
      } else {
        setSubscription({ subscribed: false, productId: null, subscriptionEnd: null, loading: false });
        // Trigger expiry flow only AFTER initial load, only if we previously
        // had a session, and only for non-user-triggered sign-outs.
        if (initialized && hadSession && event !== "SIGNED_OUT") {
          hadSession = false;
          handleSessionExpired();
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      hadSession = !!session;
      initialized = true;
      if (session) refreshSubscription();
      else setSubscription(prev => ({ ...prev, loading: false }));
    });

    return () => authSub.unsubscribe();
  }, [refreshSubscription]);

  // Auto-refresh subscription every 60s
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(refreshSubscription, 60_000);
    return () => clearInterval(interval);
  }, [session, refreshSubscription]);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      setSession(null);
      setSubscription({ subscribed: false, productId: null, subscriptionEnd: null, loading: false });
      // /offline is a public route (works signed-out), so cached trip data
      // must not survive sign-out on a shared/public device.
      clearOfflineTrips();
      toast.success(tMsg("signedOut"));
      // Hard redirect to ensure no protected page renders with stale state
      window.location.replace("/");
    }
  };

  // Plan priority: admin > paid subscription > complimentary plan > free.
  const subscriptionPlan: PlanTier = subscription.subscribed
    ? getPlanFromProductId(subscription.productId)
    : "free";
  const planRank: Record<PlanTier, number> = { free: 0, voyageur: 1, explorer: 2, pro: 3 };
  const bestNonAdmin: PlanTier = compPlan && planRank[compPlan] > planRank[subscriptionPlan]
    ? compPlan
    : subscriptionPlan;
  const plan: PlanTier = isAdmin ? "pro" : bestNonAdmin;

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, signOut, subscription, refreshSubscription, plan, isAdmin }}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            <p className="text-sm text-muted-foreground font-sans">{tMsg("appLoading")}</p>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
